/**
 * markets.js — Canonical market schema and terminology translation layer
 *
 * Single source of truth for all market definitions. Every market is defined
 * once with metadata, aliases, and classification flags.
 *
 * Layer 1: Universal markets (league-agnostic)
 *   moneyline | spread | over_under | points
 *
 * Layer 2: League-scoped prop markets (NHL + MLB)
 *   nhl:goals | nhl:assists | nhl:points | nhl:shots_on_goal | nhl:moneyline
 *   mlb:moneyline | mlb:hits | mlb:home_runs | mlb:rbis | mlb:runs | ...
 *
 * Usage:
 *   import { resolveMarket, getMarketLabel, isPlayerPropMarket } from './lib/markets';
 *   resolveMarket('Shots on Goal', 'nhl', 'fanduel') // → 'nhl:shots_on_goal'
 *   getMarketLabel({type: 'sog'}, 'nhl')             // → 'Shots on Goal'
 *   isPlayerPropMarket({type: 'sog'}, 'nhl')         // → true
 */

// ---------------------------------------------------------------------------
// Market Definition Registry — single source of truth
// ---------------------------------------------------------------------------
//
// Every market is defined once. Canonical definitions have aliases for
// resolveMarket(). Standalone legacy definitions (aliases: []) exist only
// for classification and label lookups of type IDs stored in Supabase.
//
// IMPORTANT: requiresLineInput and serverLineRequired are NOT synonymous.
// saves, blocks, hits, and pim have requiresLineInput: true but
// serverLineRequired: false because getPlayerStat() cannot resolve them yet.
// ---------------------------------------------------------------------------

const MARKET_DEFS = [
  // ─── Universal markets (league-agnostic, with aliases) ───────────────────
  {
    id: 'moneyline', league: null,
    label: 'Moneyline', short: 'ML',
    // #1548 P2-3: the caveat is for human readers of market-registry.cjs. The
    // catalog-scrape model prompt sends ids only (market-resolver.js), so the real
    // closure is isRegulationOnlyMarketTerm, which both resolvers run first.
    description: 'Straight win bet — no spread; full game: includes OT and shootout (extra innings in MLB); never a 3-way, regulation or 60-minute market',
    aliases: [
      'moneyline', 'money line', 'ml', 'to win', 'winner', 'match winner',
      'game winner', 'win', 'outright winner',
      // FanDuel
      'game moneyline', 'moneyline (incl. ot & so)',
      // BetMGM
      '2-way moneyline', 'to win (incl. ot & so)', 'to win (incl. ot)',
      '2-way moneyline (match)', 'to win (incl. ot & so) (match)', 'to win (incl. ot) (match)',
      // DraftKings
      'game lines',
    ],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: true, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    id: 'spread', league: null,
    label: 'Spread', short: 'SPR',
    description: 'Spread / handicap bet',
    aliases: [
      'spread', 'point spread', 'handicap', 'ats', 'against the spread', 'line',
      // NHL spread variants (cross-book)
      'pl', 'puck line',
      // MLB spread variants (run line) — #699 G2: universal spread, not NHL 'pl'
      'run line', 'runline', 'run-line', 'rl',
      // FanDuel
      'puck line (incl. ot & so)',
      // BetMGM
      'puck line (incl. ot)', 'puck line (incl. ot) (match)',
      // DraftKings
      'puck line spread',
    ],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    id: 'over_under', league: null,
    label: 'Over/Under', short: 'O/U',
    description: 'Over/under on total score',
    aliases: [
      'over/under', 'over under', 'over_under', 'o/u', 'total', 'game total', 'totals',
      'total goals', 'total points', 'total score',
      // FanDuel
      'total goals (incl. ot & so)', 'game total goals',
      // BetMGM
      'total goals (incl. ot)', 'total goals (incl. ot) (match)',
      // DraftKings
      'game total goals', 'total',
    ],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: false,
  },
  {
    // Universal "points" also serves as the legacy player points type ID.
    // Label/short match PLAYER_PROP_TYPES for backward compat.
    id: 'points', league: null,
    label: 'Points (O/U)', short: 'PTS',
    description: 'Team point / goal total',
    aliases: [
      'team total', 'team points', 'team score', 'team goals', 'team total goals',
      // FanDuel
      'team total goals (incl. ot & so)',
      // BetMGM
      'team total goals (incl. ot)', 'team total goals (incl. ot) (match)',
      // DraftKings
      'team total goals',
    ],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },

  // ─── NHL-scoped markets (with aliases) ───────────────────────────────────

  {
    id: 'nhl:moneyline', league: 'nhl',
    label: 'Moneyline', short: 'ML',
    description: 'NHL game winner (moneyline); includes OT and shootout; never a 3-way, regulation or 60-minute market',
    // #1548: no regulation-only (60-minute) wording may alias here -- this id is
    // graded on the final score, OT and the shootout +1 included. 'to win (reg. time
    // only)' and the 'result' family were removed (D1); the drift test in
    // reg-time-moneyline-1548.test.js refuses any alias isRegulationOnlyMarketTerm matches.
    aliases: [
      'moneyline', 'game moneyline', 'to win',
      // FanDuel
      'moneyline (incl. ot & so)', 'game moneyline',
      // BetMGM
      'moneyline (incl. ot)', 'to win (incl. ot)',
      'moneyline (incl. ot) (match)', 'to win (incl. ot) (match)',
      // DraftKings
      'moneyline',
    ],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: true, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    id: 'nhl:goals', league: 'nhl',
    label: 'Goals', short: 'G',
    description: 'Player goal(s) scored',
    aliases: [
      'goals', 'goal', 'player goals', 'goals scored',
      'anytime goal scorer', 'to score a goal', 'goal scorer',
      'to score anytime', 'score anytime',
      // FanDuel
      'anytime goalscorer', 'to score (incl. ot)',
      // BetMGM
      'player to score a goal', 'to score anytime goal',
      'anytime goal scorer', 'anytime goal scorer (match)',
      'to score anytime goal (match)', 'player to score a goal (match)',
      // DraftKings
      'anytime goal scorer', 'anytime goalscorer', 'goalscorer',
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: true, moneyline: false, statFallback: true,
    teamPick: false, playerProp: false,
  },
  {
    id: 'nhl:assists', league: 'nhl',
    label: 'Assists', short: 'AST',
    description: 'Player assist(s) recorded',
    aliases: [
      'assists', 'assist', 'player assists', 'assists recorded',
      // FanDuel
      'player to record an assist',
      // BetMGM
      'player assists', 'to record an assist',
      'player assists (match)', 'to record an assist (match)',
      // DraftKings
      'assists',
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: true, moneyline: false, statFallback: true,
    teamPick: false, playerProp: false,
  },
  {
    id: 'nhl:anytime_assist', league: 'nhl',
    label: 'Anytime Assist', short: 'ATA',
    description: 'Player to record any assist (anytime, binary)',
    aliases: [
      // Vision output strings (confirmed from production confirmed_parses)
      'anytime_assist', 'assists',
      // Sportsbook display strings (standard across DK, FD, MGM)
      'anytime assist', 'Anytime Assist', 'Anytime assist',
      // BetMGM variants
      'anytime goal assist', 'to record an assist anytime',
      'anytime assist (match)',
    ],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nhl:points', league: 'nhl',
    label: 'Points', short: 'PTS',
    description: 'Player points (goals + assists)',
    aliases: [
      'points', 'player points', 'goals + assists', 'goals and assists',
      'pts', 'nhl points',
      // FanDuel
      'points (goals + assists)', 'player points',
      // BetMGM
      'player points (goals + assists)', 'player to record a point',
      'points (goals+assists)',
      'anytime point', 'anytime point (match)',
      'player to record a point (match)', 'points (goals + assists) (match)',
      'player points (goals + assists) (match)', 'points (goals+assists) (match)',
      // DraftKings
      'player points', 'point',
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: true, moneyline: false, statFallback: true,
    teamPick: false, playerProp: false,
  },
  {
    id: 'nhl:shots_on_goal', league: 'nhl',
    label: 'Shots on Goal', short: 'SOG',
    description: 'Player shots on goal',
    aliases: [
      'shots on goal', 'shots on net', 'shots', 'sog', 'total shots',
      'shots taken',
      // FanDuel
      'player shots on goal',
      // BetMGM
      'player shots on goal', 'player to record a shot on goal',
      'player shots on goal (match)', 'shots on goal (match)',
      'player to record a shot on goal (match)',
      // DraftKings
      'shots on goal', 'player shots on goal', 'sog',
    ],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nhl:saves', league: 'nhl',
    label: 'Saves (O/U)', short: 'SAV',
    description: 'Goalie saves Over/Under',
    aliases: [
      // Vision short ID
      'saves',
      // FanDuel -- confirmed via Deep Research 2026-05-21 (high confidence)
      // #1548 P2-4: the three '(60 min)' variants were removed -- a 60-minute
      // market is not full-game saves (isRegulationOnlyMarketTerm refuses them).
      'game specials - saves',
      'goalie total saves',
      // BetMGM -- confirmed via official event page 2026-05-21 (high confidence)
      'goalie saves',
      // DraftKings -- confirmed via event snippets 2026-05-21 (high confidence)
      'saves o/u',
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nhl:blocks', league: 'nhl',
    label: 'Blocked Shots (O/U)', short: 'BLK',
    description: 'Player blocked shots',
    aliases: [
      // Vision short ID
      'blocks',
      // FanDuel ladder format
      'player to record 1+ blocked shots',
      'player to record 2+ blocked shots',
      'player to record 3+ blocked shots',
      'blocked shots',
      // BetMGM
      'player blocked shots',
      // DraftKings
      'blocks', 'blocks o/u',
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nhl:hits', league: 'nhl',
    label: 'Hits (O/U)', short: 'HIT',
    description: 'Player hits',
    aliases: [
      // Vision short ID
      'hits',
      // FanDuel + BetMGM
      'player hits',
      // DraftKings -- NOT confirmed. Stub only.
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nhl:pim', league: 'nhl',
    label: 'Penalty Minutes (O/U)', short: 'PIM',
    description: 'Penalty minutes (PIM)',
    aliases: [
      'pim', 'penalty minutes',
    ],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },

  // ─── MLB-scoped markets (with aliases) ───────────────────────────────────

  {
    id: 'mlb:moneyline', league: 'mlb',
    label: 'Moneyline', short: 'ML',
    description: 'MLB game winner (moneyline)',
    aliases: ['moneyline', 'money line', 'ml', 'game lines', 'winner'],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: true, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    id: 'mlb:hits', league: 'mlb',
    label: 'Hits (O/U)', short: 'H',
    description: 'Batter hits over/under',
    aliases: ['hits', 'batter hits', 'player hits', 'total hits'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  // BetMGM: ladder format (1+, 2+) -- vision extracts threshold as line field
  // FanDuel: "To Hit a Home Run" -- treated as line: 1, Over implied
  // No traditional O/U confirmed for any book. Option B pattern (same as nhl:blocks).
  // serverLineRequired: true kept intentionally — Stage D INVALID_LINE_FOR_MARKET
  // catches legs where vision fails to extract line, preventing silent pending-after-FINAL.
  // Research confirmed: 2026-05-26
  // NFL markets (#1038 registered; #1040 PR C opened settlement for five, PR D
  // opened nfl:total (5a) and nfl:spread (5b) for seven) -----------
  // LOAD-BEARING FLAG DISCIPLINE: calcStatus does NOT consult marketSupportStatus() --
  // it gates on SETTLEABLE_LEAGUES, then on GRADABLE_MARKETS_BY_LEAGUE.nfl (constants.js),
  // then on the capability sets built from these flags. The flags therefore decide
  // WHICH grading branch an admitted market takes:
  //   nfl:moneyline      moneyline:true  -> score-diff branch (isMoneylineMarket, betting.js)
  //   the four O/U props enrichable:true -> frozen_stat is REQUIRED at FINAL: every
  //                      missed-freeze guard keys on isEnrichablePlayerProp
  //                      (settlement-processor.js hydrate + ambiguous_frozen_stat guards,
  //                      settlement-leg-status.js, rawParlayLegStatuses). A settleable
  //                      market that is NOT enrichable would grade from nothing in the
  //                      FINAL-before-freeze race and be written terminally (Ultron P0-1).
  //   nfl:anytime_td     enrichable:true since #1353 PR 2 (frozen_stat REQUIRED at FINAL
  //                      like the O/U props; the count is the reconciled scorer count).
  //                      nfl:total (5a) and nfl:spread (5b) are
  //                      graded since #1040 PR D -- both would otherwise hit the bare
  //                      `spread` / over_under branches, which grade by score with no
  //                      league gate; first/last TD -> scoringPlays spike.
  //   statFallback:false everywhere.
  //
  // Aliases deliberately include the universal terms (moneyline, spread, total)
  // so resolveMarket()'s league-scoped pass claims them as `nfl:*` BEFORE the
  // universal fallthrough can yield a bare `moneyline` or `spread` -- the latter
  // hits an UNGATED `market === "spread"` branch in betting.js.
  {
    id: 'nfl:moneyline', league: 'nfl',
    label: 'Moneyline (NFL)', short: 'ML',
    description: 'NFL straight win bet',
    aliases: ['moneyline', 'money line', 'ml', 'to win', 'winner', 'game winner'],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: true, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    // Graded since #1040 PR D (5b).
    id: 'nfl:spread', league: 'nfl',
    label: 'Spread (NFL)', short: 'SPR',
    description: 'NFL point spread',
    aliases: ['spread', 'point spread', 'handicap', 'ats', 'against the spread'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    // Graded since #1040 PR D (5a).
    id: 'nfl:total', league: 'nfl',
    label: 'Total (NFL)', short: 'TOT',
    description: 'NFL combined game total over/under',
    aliases: ['total', 'total points', 'over/under', 'over under', 'game total'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    // teamPick FALSE -- an over/under pick is 'Over'/'Under', not a team. The
    // universal over_under def sets it false for the same reason. Pre-Codex R2: it
    // was true here, and isTeamPickMarket ignores league entirely, so normalizeLegPick
    // ran normalizeTeamToken('Over','nfl') and the `aliases[upper] || upper` fallback
    // persisted the pick as 'OVER'. It failed closed (calcStatus's O/U branch rejects
    // "OVER") but the stored pick was corrupt and unrecoverable -- the same argument
    // that split nfl:last_td out.
    //
    // THIS FLAG IS NOW LOAD-BEARING, NOT BELT-AND-BRACES. At the time of R2 the
    // reason the fallback misfired was that api/lib/team-aliases.js had NO nfl
    // entries. NFL_TEAM_ALIASES has since landed, so normalizeTeamToken is ACTIVE
    // for nfl, and 'Over' -> 'OVER' goes live again the instant this flips to true.
    // Do not "simplify" it back on the assumption the hazard is hypothetical.
    teamPick: false, playerProp: false,
  },
  {
    id: 'nfl:passing_yards', league: 'nfl',
    label: 'Passing Yards (O/U)', short: 'PYD',
    description: 'QB passing yards over/under (net -- sack yardage deducted)',
    aliases: ['passing yards', 'pass yards', 'player passing yards', 'pass yds'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nfl:rushing_yards', league: 'nfl',
    label: 'Rushing Yards (O/U)', short: 'RYD',
    description: 'Rushing yards over/under',
    aliases: ['rushing yards', 'rush yards', 'player rushing yards', 'rush yds'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nfl:receiving_yards', league: 'nfl',
    label: 'Receiving Yards (O/U)', short: 'REYD',
    description: 'Receiving yards over/under',
    aliases: ['receiving yards', 'rec yards', 'player receiving yards', 'rec yds'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nfl:receptions', league: 'nfl',
    label: 'Receptions (O/U)', short: 'REC',
    description: 'Receptions over/under',
    aliases: ['receptions', 'player receptions', 'catches', 'recs'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nfl:anytime_td', league: 'nfl',
    label: 'Anytime TD Scorer', short: 'ATD',
    description: 'Player is credited with a touchdown (rushing, receiving, return or defensive) at any point',
    // SETTLEMENT RULE (#1353 PR 2, spec §6): any TD credited to the athlete as the ESPN core
    // plays `scorer`, reconciled against the summary box score under C1-C9 (src/lib/nfl.js
    // reconcileTouchdowns) and read by roster id. Passing TDs never count (the passer is not
    // the scorer). Graded 1+ only through the binary branch (touchdowns >= 1 won, 0 lost).
    aliases: ['anytime touchdown', 'anytime td', 'to score a touchdown',
              'anytime touchdown scorer', 'td scorer', 'anytime td scorer'],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nfl:first_td', league: 'nfl',
    label: 'First TD Scorer', short: 'FTD',
    description: 'Player scores the first rushing/receiving TD of the game',
    // Same passingTouchdowns exclusion as nfl:anytime_td.
    //
    // SPLIT FROM last_td in pre-Codex R1. These were one def whose aliases covered
    // both, so a LAST-TD bet was persisted as `nfl:first_td` with nothing on the row
    // to distinguish it -- the first-vs-last distinction destroyed at write time and
    // unrecoverable for every row imported during the fail-closed window. When #1040
    // grades nfl:first_td as "first TD", every one of those legs would grade against
    // the wrong event. Fail-closed storage still has to store the RIGHT thing.
    aliases: ['first touchdown', 'first td', 'first touchdown scorer',
              'first td scorer'],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nfl:last_td', league: 'nfl',
    label: 'Last TD Scorer', short: 'LTD',
    description: 'Player scores the last rushing/receiving TD of the game',
    // Same passingTouchdowns exclusion as nfl:anytime_td.
    aliases: ['last touchdown', 'last td', 'last touchdown scorer',
              'last td scorer'],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'mlb:home_runs', league: 'mlb',
    label: 'Home Runs (O/U)', short: 'HR',
    description: 'Batter home runs over/under',
    aliases: [
      'home runs', 'home run', 'hr', 'player home runs',
      'to hit a home run',          // FanDuel confirmed anytime binary
      'anytime home run',           // generic
      'batter home runs',           // BetMGM category label
      '1+ home runs',               // BetMGM ladder tier 1 (vision extracts line: 1)
      '2+ home runs',               // BetMGM ladder tier 2 (vision extracts line: 2)
    ],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'mlb:rbis', league: 'mlb',
    label: 'RBIs (O/U)', short: 'RBI',
    description: 'Batter runs batted in',
    aliases: ['rbis', 'rbi', 'runs batted in', 'player rbis', 'batter rbis'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'mlb:runs', league: 'mlb',
    label: 'Runs Scored (O/U)', short: 'R',
    description: 'Batter runs scored',
    aliases: ['runs', 'runs scored', 'player runs', 'batter runs'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'mlb:total_bases', league: 'mlb',
    label: 'Total Bases (O/U)', short: 'TB',
    description: 'Batter total bases',
    aliases: ['total bases', 'player total bases', 'batter total bases', 'tb'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'mlb:strikeouts_pitcher', league: 'mlb',
    label: 'Pitcher Strikeouts (O/U)', short: 'K',
    description: 'Pitcher strikeouts',
    aliases: ['strikeouts', 'pitcher strikeouts', 'player strikeouts pitcher', 'ks', 'total strikeouts', 'strikeouts recorded'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'mlb:strikeouts_batter', league: 'mlb',
    label: 'Batter Strikeouts (O/U)', short: 'KS',
    description: 'Batter strikeouts',
    aliases: ['batter strikeouts', 'player strikeouts batter', 'strikeouts batter'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },

  // ─── NBA-scoped markets (#1655 B1: registered, ALL UNSUPPORTED_FAIL_CLOSED) ───
  // Plan docs/audits/2026-10-30-1655-b1-plan.md (+ its Ultron dispositions table).
  // The job of these defs is to give every NBA leg a STORED id no grading path grades,
  // and that D1/D2 can later grade from WITHOUT reprocessing -- so each alias must
  // store the RIGHT id, not just a fail-closed one (#1045's last_td lesson).
  //
  // LOAD-BEARING FLAG DISCIPLINE (copy of the #1038 NFL pre-settlement rule):
  // moneyline / enrichable / statFallback / binary are FALSE on every def. calcStatus
  // never reads marketSupportStatus(); it gates on SETTLEABLE_LEAGUES first (nba is not
  // in it -- betting.js), then on the capability sets these flags build. binary:true
  // in particular feeds the liveStatVal branch that #1045 found grading an
  // UNSUPPORTED nfl:anytime_td. Flip a flag only in the D1/D2 PR that admits the id.
  //
  // serverLineRequired / requiresLineInput stay TRUE on nba:spread and nba:total, as on
  // nfl:spread / nfl:total (Ultron P1-2): isLineRequired + teamPick is what lets
  // normalizeLegPick recover "Boston Celtics -3.5" -> line -3.5, and nba:spread is in
  // spread-sign.js SIGNED_LINE_MARKETS for the #1132 sign gate. A spread stored with a
  // dropped minus would be graded wrong by D1 with no way back.
  //
  // Alias traps (plan §1, Ultron H1):
  //  - NO bare 'total points' anywhere. Once a player/team prefix is stripped,
  //    "Celtics Total Points" (team total, #962) becomes "total points"; it must
  //    resolve to null, never nba:total (game total) or nba:points (player).
  //  - no team-total wording ('team total', 'team points') -- the universal `points`
  //    id is carved out of nba and has no NBA analogue yet.
  //  - quarter / half / first-basket wording never aliases here; resolveMarket refuses
  //    period-scoped text for nba before the lookup (isPeriodScopedMarketTerm).
  // Aliases are re-pinned against real book wording from the first
  // `[slipsync-mgm] unsupported_league` NBA capture (Ultron H1).
  {
    id: 'nba:moneyline', league: 'nba',
    label: 'Moneyline (NBA)', short: 'ML',
    description: 'NBA straight win bet; full game including overtime',
    aliases: ['moneyline', 'money line', 'ml', 'to win', 'winner', 'game winner',
              'moneyline (incl. ot)', 'to win (incl. ot)'],
    requiresLineInput: false, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    id: 'nba:spread', league: 'nba',
    label: 'Spread (NBA)', short: 'SPR',
    description: 'NBA point spread; full game including overtime',
    aliases: ['spread', 'point spread', 'handicap', 'ats', 'against the spread',
              'spread (incl. ot)'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: true, playerProp: false,
  },
  {
    id: 'nba:total', league: 'nba',
    label: 'Total (NBA)', short: 'TOT',
    description: 'NBA combined game total over/under; full game including overtime',
    // No 'total points' (Ultron H1, see block comment above).
    aliases: ['total', 'game total', 'over/under', 'over under', 'totals',
              'total (incl. ot)'],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    // teamPick FALSE -- an over/under pick is 'Over'/'Under', not a team (the
    // nfl:total R2 lesson: a true flag runs normalizeTeamToken on 'Over').
    teamPick: false, playerProp: false,
  },
  {
    id: 'nba:points', league: 'nba',
    label: 'Points (O/U)', short: 'PTS',
    description: 'Player points scored over/under',
    aliases: ['points', 'player points', 'pts', 'points scored'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:rebounds', league: 'nba',
    label: 'Rebounds (O/U)', short: 'REB',
    description: 'Player total rebounds over/under',
    aliases: ['rebounds', 'player rebounds', 'reb', 'rebs'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:assists', league: 'nba',
    label: 'Assists (O/U)', short: 'AST',
    description: 'Player assists over/under',
    aliases: ['assists', 'player assists', 'ast', 'asts'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:threes_made', league: 'nba',
    label: 'Threes Made (O/U)', short: '3PM',
    description: 'Player three-point field goals made over/under',
    // MADE only: "3-pointers attempted" is a different stat and must not alias here.
    aliases: ['threes', 'threes made', 'made threes', '3-pointers made', '3 pointers made',
              'three pointers made', 'three-pointers made', '3-point field goals made',
              '3pm', 'player threes', 'player threes made', 'player 3-pointers made'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:pra', league: 'nba',
    label: 'Pts + Reb + Ast (O/U)', short: 'PRA',
    description: 'Player points + rebounds + assists over/under',
    aliases: ['pra', 'pts+reb+ast', 'pts + reb + ast', 'points + rebounds + assists',
              'points, rebounds & assists', 'points, rebounds and assists',
              'player points + rebounds + assists'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:points_rebounds', league: 'nba',
    label: 'Pts + Reb (O/U)', short: 'P+R',
    description: 'Player points + rebounds over/under',
    aliases: ['pts+reb', 'pts + reb', 'points + rebounds', 'points & rebounds',
              'points and rebounds', 'player points + rebounds'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:points_assists', league: 'nba',
    label: 'Pts + Ast (O/U)', short: 'P+A',
    description: 'Player points + assists over/under',
    aliases: ['pts+ast', 'pts + ast', 'points + assists', 'points & assists',
              'points and assists', 'player points + assists'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },
  {
    id: 'nba:rebounds_assists', league: 'nba',
    label: 'Reb + Ast (O/U)', short: 'R+A',
    description: 'Player rebounds + assists over/under',
    aliases: ['reb+ast', 'reb + ast', 'rebounds + assists', 'rebounds & assists',
              'rebounds and assists', 'player rebounds + assists'],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true,
  },

  // ─── Standalone legacy type IDs (no aliases, classification only) ────────
  // These IDs are stored in Supabase bet_legs.type from legacy imports.
  // They don't participate in resolveMarket() — only in Set membership and
  // label/short lookups.

  { id: 'ml', league: 'nhl', label: 'Moneyline', short: 'ML',
    description: 'Moneyline (legacy)', aliases: [],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: true, statFallback: false,
    teamPick: true, playerProp: false },
  { id: 'ou', league: 'nhl', label: 'Over/Under', short: 'O/U',
    description: 'Over/Under (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: false },
  { id: 'pl', league: 'nhl', label: 'Puck Line', short: 'PL',
    description: 'Puck Line', aliases: [],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: false, statFallback: false,
    teamPick: true, playerProp: false },
  { id: 'ftts', league: 'nhl', label: 'First Team to Score', short: 'FTS',
    description: 'First Team to Score', aliases: [],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: false, moneyline: false, statFallback: false,
    teamPick: true, playerProp: false },
  { id: 'sog', league: 'nhl', label: 'Shots on Goal', short: 'SOG',
    description: 'Shots on Goal (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'anytime_goal', league: 'nhl', label: 'Anytime Goalscorer', short: 'ATG',
    description: 'Anytime Goalscorer', aliases: [],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'anytime_point', league: 'nhl', label: 'Anytime Point', short: 'ATP',
    description: 'Anytime Point', aliases: [],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'anytime_assist', league: 'nhl', label: 'Anytime Assist', short: 'ATA',
    description: 'Anytime Assist (legacy)', aliases: [],
    requiresLineInput: false, serverLineRequired: false,
    binary: true, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'goals', league: 'nhl', label: 'Goals', short: 'G',
    description: 'Goals (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: false },
  { id: 'assists', league: 'nhl', label: 'Assists (O/U)', short: 'AST',
    description: 'Assists (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: true,
    binary: false, enrichable: true, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'saves', league: 'nhl', label: 'Saves (O/U)', short: 'SAV',
    description: 'Saves (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'blocks', league: 'nhl', label: 'Blocked Shots (O/U)', short: 'BLK',
    description: 'Blocked Shots (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'hits', league: 'nhl', label: 'Hits (O/U)', short: 'HIT',
    description: 'Hits (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
  { id: 'pim', league: 'nhl', label: 'Penalty Minutes (O/U)', short: 'PIM',
    description: 'Penalty Minutes (legacy)', aliases: [],
    requiresLineInput: true, serverLineRequired: false,
    binary: false, enrichable: false, moneyline: false, statFallback: false,
    teamPick: false, playerProp: true },
];

// ---------------------------------------------------------------------------
// Internal — build structures from definitions at module load time
// ---------------------------------------------------------------------------

// Definition index: id → definition (for label/classification lookups)
const _definitionIndex = new Map();
for (const def of MARKET_DEFS) {
  _definitionIndex.set(def.id, def);
}

// Rebuild UNIVERSAL_MARKETS and NHL_MARKETS for resolveMarket() and
// describeMarket() backward compatibility. Only canonical definitions
// (those with aliases) contribute.
const UNIVERSAL_MARKETS = {};
const NHL_MARKETS = {};
for (const def of MARKET_DEFS) {
  if (def.aliases.length === 0) continue;
  if (def.league === null) {
    UNIVERSAL_MARKETS[def.id] = def;
  } else if (def.league === 'nhl' && def.id.startsWith('nhl:')) {
    NHL_MARKETS[def.id.replace('nhl:', '')] = def;
  }
}

/** Normalise a raw term for alias matching: lowercase, trim, collapse spaces. */
function normaliseTerm(term) {
  return String(term)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Build a Map from normalised alias → canonical key.
 * First alias registered wins; duplicates are silently skipped.
 */
function buildLookup(markets, ns) {
  const map = new Map();
  for (const [key, market] of Object.entries(markets)) {
    const canonical = ns ? `${ns}:${key}` : key;
    for (const alias of market.aliases) {
      const norm = normaliseTerm(alias);
      if (!map.has(norm)) {
        map.set(norm, canonical);
      }
    }
  }
  return map;
}

const _universalLookup = buildLookup(UNIVERSAL_MARKETS, null);
const _nhlLookup       = buildLookup(NHL_MARKETS, 'nhl');

// Build MLB lookup directly from MARKET_DEFS (MLB_MARKETS export is defined later)
const _mlbMarketsInternal = Object.fromEntries(
  MARKET_DEFS
    .filter(d => d.league === 'mlb' && d.id.startsWith('mlb:') && d.aliases.length > 0)
    .map(d => [d.id.replace('mlb:', ''), d])
);
const _mlbLookup = buildLookup(_mlbMarketsInternal, 'mlb');

// #1038: NFL lookup, same pattern as MLB.
const _nflMarketsInternal = Object.fromEntries(
  MARKET_DEFS
    .filter(d => d.league === 'nfl' && d.id.startsWith('nfl:') && d.aliases.length > 0)
    .map(d => [d.id.replace('nfl:', ''), d])
);
const _nflLookup = buildLookup(_nflMarketsInternal, 'nfl');

// #1655 B1: NBA lookup, same pattern as NFL.
const _nbaMarketsInternal = Object.fromEntries(
  MARKET_DEFS
    .filter(d => d.league === 'nba' && d.id.startsWith('nba:') && d.aliases.length > 0)
    .map(d => [d.id.replace('nba:', ''), d])
);
const _nbaLookup = buildLookup(_nbaMarketsInternal, 'nba');

// #1038 / #1655 B1: leagues whose markets are LEAGUE-SCOPED ONLY. For these, universal
// (league:null) defs never fold into the capability sets (deriveLeagueCapabilities)
// and resolveMarket never consults the universal alias table -- an unmatched term is
// null, never a bare `moneyline` / `spread` / `over_under`. One named set for both so
// the capability carve-out and the resolver containment cannot disagree about which
// leagues they cover.
const LEAGUE_SCOPED_ONLY_LEAGUES = new Set(['nfl', 'nba']);

// ---------------------------------------------------------------------------
// Legacy display alias map — canonical → legacy short ID
// ---------------------------------------------------------------------------

const LEGACY_MARKET_ALIAS = {
  moneyline: 'ml',
  'nhl:moneyline': 'ml',
  over_under: 'ou',
  'nhl:shots_on_goal': 'sog',
  'nhl:goals': 'goals',
  'nhl:assists': 'assists',
  'nhl:points': 'points',
  'nhl:anytime_assist': 'anytime_assist',
  'nhl:saves': 'saves',
  'nhl:blocks': 'blocks',
  'nhl:hits': 'hits',
  'nhl:pim': 'pim',
};

// ---------------------------------------------------------------------------
// League Capability Registry — derived from MARKET_DEFS at module load time
// ---------------------------------------------------------------------------

function deriveLeagueCapabilities(league) {
  const caps = {
    knownVisionTypeIds: new Set(),
    binaryMarkets: new Set(),
    lineRequiredMarkets: new Set(),
    enrichablePlayerProps: new Set(),
    moneylineMarkets: new Set(),
    statFallbackMarkets: new Set(),
  };

  for (const def of MARKET_DEFS) {
    // #324 Rung 2: universal (league:null) definitions fold into EVERY league's
    // capability set — not NHL-only — so a universal-id resolution (e.g. `moneyline`
    // for MLB) settles via the same league-agnostic calcStatus branch regardless of
    // league, structurally closing the §2.4 pending-after-FINAL footgun for MLB
    // expansion (see docs/specs/2026-08-04-324-market-resolution-two-system-coordination.md).
    // EXCEPTION — universal `points` folds into NHL ONLY (its historical player-prop
    // home; pre-Rung2 behavior preserved verbatim by the `league === 'nhl'` clause).
    // Its enrichable:true/playerProp:true flags are correct for NHL but would
    // incorrectly route MLB team-total `points` into the enrichable-prop settlement
    // path. Tracked: #962 — remove the carve-out only after the `points` canonical is
    // split into separate team-total and player-prop definitions.
    const universal = def.league === null;
    // #1038 NFL CARVE-OUT -- LOAD-BEARING, DO NOT REMOVE. Still true post-#1040
    // (spec §4.3): universal ids stay out of NFL BY DESIGN, permanently --
    // GRADABLE_MARKETS_BY_LEAGUE.nfl (constants.js) is now the positive admission
    // list, and calcStatus gates on THESE per-league capability sets, not on
    // marketSupportStatus(): calcStatus (betting.js) grades a leg when its
    // isMoneylineMarket(market, league) check is true. Folding universal `moneyline` /
    // `spread` / `over_under` into NFL would let a bare canonical reach the
    // league-agnostic score-diff / spread / total branches with NO admission
    // check -- exactly the escape PR D's league-scoped `nfl:*` ids close. Bare
    // `spread` / `over_under` / `moneyline` therefore stay refused under nfl even
    // after nfl:total / nfl:spread are admitted (D3): the LITERAL `nfl:spread` /
    // `nfl:total` / `nfl:moneyline` ids are what's gated in, never the universal
    // fallthrough. The `nfl:spread` literal itself is still ungated for every
    // OTHER league (same math as any other league-scoped id) -- this carve-out is
    // NFL-only.
    // #1655 B1: the carve-out now covers every LEAGUE_SCOPED_ONLY_LEAGUES member, so nba
    // gets the same exclusion -- bare `moneyline` / `spread` / `over_under` must never
    // be capability-recognised for nba, or D1 admitting nba would open the
    // league-agnostic score branches to a bare id. NFL membership (and so NFL
    // behaviour) is unchanged.
    // Same shape as the `points` NHL-only carve-out below it.
    const include =
      def.league === league ||
      (universal && !LEAGUE_SCOPED_ONLY_LEAGUES.has(league) && (league === 'nhl' || def.id !== 'points'));
    if (!include) continue;

    caps.knownVisionTypeIds.add(def.id);
    if (def.binary) caps.binaryMarkets.add(def.id);
    if (def.serverLineRequired) caps.lineRequiredMarkets.add(def.id);
    if (def.enrichable) caps.enrichablePlayerProps.add(def.id);
    if (def.moneyline) caps.moneylineMarkets.add(def.id);
    if (def.statFallback) caps.statFallbackMarkets.add(def.id);
  }

  return caps;
}

const LEAGUE_CAPABILITIES = {
  nhl: deriveLeagueCapabilities('nhl'),
  mlb: deriveLeagueCapabilities('mlb'),
  // #1038: required. normalizeLeague() returns null for any league absent here,
  // which would short-circuit marketSupportStatus() to 'UNKNOWN' and make the
  // whole _MARKET_SUPPORT_STATUS.nfl table dead code.
  nfl: deriveLeagueCapabilities('nfl'),
  // #1655 B1: same reason as nfl -- without it normalizeLeague('nba') is null,
  // marketSupportStatus is 'UNKNOWN' for every NBA market and the import warning reads
  // "unknown market" instead of "Can't auto-grade". Registering nba HERE grades
  // nothing: calcStatus gates on SETTLEABLE_LEAGUES (constants.js) before any
  // capability set is read, and every nba def's grading flags are false.
  nba: deriveLeagueCapabilities('nba'),
};

const EMPTY_CAPABILITIES = {
  knownVisionTypeIds: new Set(),
  binaryMarkets: new Set(),
  lineRequiredMarkets: new Set(),
  enrichablePlayerProps: new Set(),
  moneylineMarkets: new Set(),
  statFallbackMarkets: new Set(),
};

function getLeague(league) {
  if (league == null) return EMPTY_CAPABILITIES;
  const key = String(league).toLowerCase().trim();
  if (!key) return EMPTY_CAPABILITIES;
  // #1040 pre-Codex r1 (Investigate): `LEAGUE_CAPABILITIES[key] ||
  // EMPTY_CAPABILITIES` resolves inherited Object.prototype members for
  // key === 'constructor' / '__proto__' (both truthy), returning a function
  // or the prototype object instead of EMPTY_CAPABILITIES -- every caller
  // (isLineRequired, isBinaryMarket, etc.) then throws reading a Set method
  // off it. hasOwnProperty guards against own-vs-inherited lookup.
  return Object.prototype.hasOwnProperty.call(LEAGUE_CAPABILITIES, key)
    ? LEAGUE_CAPABILITIES[key]
    : EMPTY_CAPABILITIES;
}

// ---------------------------------------------------------------------------
// Display market type lists — derived from definitions
// ---------------------------------------------------------------------------

const _displayTeamIds = ['ml', 'spread', 'ou', 'pl', 'ftts'];
const _displayPlayerIds = [
  'anytime_goal', 'anytime_point', 'anytime_assist',
  'sog', 'points', 'assists', 'saves', 'blocks', 'hits', 'pim',
];
const _displayMlbPlayerIds = [
  'mlb:hits',
  'mlb:home_runs',
  'mlb:rbis',
  'mlb:runs',
  'mlb:total_bases',
  'mlb:strikeouts_pitcher',
  'mlb:strikeouts_batter',
];

// ---------------------------------------------------------------------------
// Public API — existing functions (preserved)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// #1548 -- regulation-only (60-minute) market wording
// ---------------------------------------------------------------------------
// Every market id SlipTrack grades on a final score counts OT and the shootout.
// A regulation-only market settles on the 60-minute score (all three books' house
// rules), so an OT/SO win grades won here and lost at the book. No such market has
// a grader (docs/markets-unmapped.md), so these terms must never resolve.

// Substring signals, matched on the lowercased, space-collapsed term.
const REGULATION_ONLY_PATTERNS = [
  /\bregulation\b/,                                  // regulation (time)
  /\breg(?:ular)?\.?[\s-]*time\b/,                    // reg time, reg. time, regular time, regular-time
  /\b60[\s-]*min(?:ute)?s?\b/,                        // 60 min, 60-min, 60min, 60 minute(s)
  /\b(?:3|three)[\s-]*way\b/,                          // 3-way, 3 way, 3way, three-way
  /\b1x2\b/,                                          // 1X2
  /\bexcl(?:\.|uding|udes?)?\s*(?:ot|overtime)\b/,     // excl. ot, excl ot, excluding overtime
  /\b(?:without|no)\s+(?:ot|overtime)\b/,              // without ot, no ot, no overtime
  /\b(?:draw|tie)\s+no\s+bet\b/,                       // draw no bet, tie no bet (60-minute derivatives)
  /\bdouble\s+chance\b/,                              // double chance (60-minute derivative)
  // #1548 pre-Codex (Investigate LOW): further regulation-only wording, each checked
  // against every alias (drift test) and the full-game controls.
  /\(\s*reg\.?\s*\)/,                                  // (reg), (reg.) -- parenthesised only, never a bare word
  /\bfull[\s-]*time\s+result\b/,                       // full time result, full-time result (1X2 wording)
  /\b(?:3|three)[\s-]*options?\b/,                      // 3 option(s), 3-option, three options
  /\bnot\s+incl(?:\.|uding|udes?)?\s*(?:ot|overtime)\b/, // not incl. ot, not including overtime
];

// #1548 D1 / P1-2: exact terms only. Assumed (D1, unverified): a bare 'Result' is
// the 3-way 60-minute market; fail closed until a BetMGM NHL 'Result' payload settles
// it. Exact, so a future "Result (incl. OT)" is not refused.
const REGULATION_ONLY_EXACT_TERMS = new Set(['result', 'game result', 'match result', '1x2']);
// #1548 pre-Codex F1: BetMGM plain (non-SGP) market names carry a "(Match)" suffix
// ("2-way moneyline (match)"), so ONE trailing "(match)" is stripped before the
// exact-set comparison only -- "Result (Match)" is refused, "Result (incl. OT)" is not.
const TRAILING_MATCH_SUFFIX = /\s*\(match\)$/;

/**
 * Single job: return true when a raw market or selection text carries wording that
 * marks a regulation-only (60-minute) market, which no SlipTrack market id grades.
 * Pure and synchronous. Non-ASCII characters are tried both removed and as a space,
 * so a zero-width character can hide neither an exact term nor a signal.
 */
export function isRegulationOnlyMarketTerm(term) {
  if (typeof term !== 'string' || !term) return false;
  const variants = new Set([
    normaliseTerm(term.replace(/[^\x20-\x7E]/g, '')),
    normaliseTerm(term.replace(/[^\x20-\x7E]/g, ' ')),
  ]);
  for (const v of variants) {
    if (REGULATION_ONLY_EXACT_TERMS.has(v) || REGULATION_ONLY_EXACT_TERMS.has(v.replace(TRAILING_MATCH_SUFFIX, ''))) return true;
    if (REGULATION_ONLY_PATTERNS.some((re) => re.test(v))) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// #1655 B1 -- period-scoped (half / quarter) market wording
// ---------------------------------------------------------------------------
// Every nba:* id means the FULL GAME. A 1st-half spread or a 3rd-quarter total settles
// on a different score, and D1/D2 will grade from the stored id with no reprocessing,
// so period wording must store null, never a full-game id (Ultron P1-1). Applied to
// nba only (resolveMarket's nba pass, import-confirm's nba storage); nhl/mlb/nfl
// resolution is unchanged by it.
const PERIOD_SCOPED_PATTERNS = [
  /\bhalf\b/,               // 1st half, 2nd half, first half, second half, half
  /\bhalftime\b/,           // halftime
  /\b[12]h\b/,              // 1H, 2H
  /\bquarters?\b/,          // 1st quarter ... 4th quarter, quarter
  /\bqtr\b/,                // 1st qtr
  /\b[1-4]q\b/,             // 1Q .. 4Q
  /\bq[1-4]\b/,             // Q1 .. Q4
];

/**
 * Single job: return true when a raw market text carries half or quarter wording,
 * which marks a period-scoped market that no full-game market id may store.
 * Pure and synchronous. Non-ASCII characters are tried both removed and as a space,
 * as isRegulationOnlyMarketTerm does.
 */
export function isPeriodScopedMarketTerm(term) {
  if (typeof term !== 'string' || !term) return false;
  const variants = new Set([
    normaliseTerm(term.replace(/[^\x20-\x7E]/g, '')),
    normaliseTerm(term.replace(/[^\x20-\x7E]/g, ' ')),
  ]);
  for (const v of variants) {
    if (PERIOD_SCOPED_PATTERNS.some((re) => re.test(v))) return true;
  }
  return false;
}

/**
 * Resolve a raw sportsbook term to a canonical SlipTrack market key.
 *
 * Resolution order (when league provided):
 *   0. Regulation-only wording (#1548) -- null, before any lookup, every league
 *   1. League-scoped aliases (e.g. NHL props)
 *   2. Universal aliases
 *   3. null — unrecognised term, logged for training review
 */
export function resolveMarket(term, league, sportsbook) {
  if (!term) return null;

  const norm      = normaliseTerm(term);
  const leagueKey = league    ? String(league).toLowerCase().trim()    : null;
  const bookKey   = sportsbook ? String(sportsbook).toLowerCase().trim() : null;

  // 0. #1548: a regulation-only term never reaches a lookup, so no alias (present or
  // future) can map it onto a full-game id. It falls to the step-3 miss log, tagged.
  const regulationOnly = isRegulationOnlyMarketTerm(String(term));

  // 1. League-scoped lookup
  if (!regulationOnly && leagueKey === 'nhl') {
    const match = _nhlLookup.get(norm);
    if (match) return match;
  }
  if (!regulationOnly && leagueKey === 'mlb') {
    const match = _mlbLookup.get(norm);
    if (match) return match;
  }
  // #1038 (GPT pre-impl audit P1-1): NFL TERMINATES HERE -- it must NEVER fall
  // through to the universal lookup below.
  //
  // A league-scoped pass that still falls through is only as safe as its alias
  // coverage, which is not a boundary. Measured on the first attempt: `line`,
  // `o/u`, `totals`, `total score`, `match winner`, `win`, `outright winner` and
  // `game lines` all escaped to bare `spread` / `over_under` / `moneyline` --
  // canonicals that calcStatus grades with NO league guard (betting.js:173 is a
  // literal `market === "spread"` check). Returning null instead makes the
  // boundary structural: an unrecognised NFL term is unresolved, never
  // gradeable. Do not reintroduce the fallthrough by "just adding the missing
  // aliases" -- that is the failure mode this replaced.
  if (!regulationOnly && leagueKey === 'nfl') {
    // Terminates -- no universal fallthrough (that is the P1-1 containment).
    // But fall THROUGH TO THE SHARED LOGGING on a miss: NFL ships 10 defs against
    // books that name hundreds of markets, and every unrecognised term is stored
    // NULL. Silently. That is precisely the population that needs training review,
    // and it was the one population generating no resolver log line.
    const nflHit = _nflLookup.get(norm);
    if (nflHit) return nflHit;
  }
  // #1655 B1: NBA terminates here exactly like NFL (same P1-1 containment): NBA text
  // yields an nba:* id or null, never a bare universal id. Period-scoped wording
  // (half / quarter) never reaches the lookup, so no alias can map "1st Half Spread"
  // or "Q1 Total" onto a full-game id.
  const periodScoped = leagueKey === 'nba' && isPeriodScopedMarketTerm(String(term));
  if (!regulationOnly && !periodScoped && leagueKey === 'nba') {
    const nbaHit = _nbaLookup.get(norm);
    if (nbaHit) return nbaHit;
  }

  // 2. Universal lookup -- SKIPPED ENTIRELY FOR NFL AND NBA (LEAGUE_SCOPED_ONLY_LEAGUES).
  // The containment above is a structural boundary, not an alias-coverage exercise: a
  // miss must reach the training log below WITHOUT ever consulting the universal table.
  if (!regulationOnly && !LEAGUE_SCOPED_ONLY_LEAGUES.has(leagueKey)) {
    const match = _universalLookup.get(norm);
    if (match) return match;
  }

  // 3. No match — log for training review
  const ctx = [
    leagueKey  ? `league=${leagueKey}`   : null,
    bookKey    ? `sportsbook=${bookKey}` : null,
    regulationOnly ? 'reason=regulation_only' : null,
    periodScoped ? 'reason=period_scoped' : null,
  ].filter(Boolean);
  // Sanitized: `term` is untrusted client text (leg.market / leg.type from the
  // confirm payload) and lands in Vercel function logs. Control characters would
  // forge log lines and length is otherwise unbounded. Mirrors what the sibling
  // resolver already does to the same input class
  // (api/lib/slipsync/market-resolver.js). This matters more now that every
  // unresolved NFL term is routed here deliberately.
  const safeTerm = String(term).replace(/[^\x20-\x7E]/g, '').slice(0, 200);
  console.log(`[markets] unresolved term: "${safeTerm}"`, ...ctx);

  return null;
}

/**
 * Return the human-readable description for a canonical market key.
 */
export function describeMarket(canonicalKey) {
  if (!canonicalKey) return null;

  const def = _definitionIndex.get(canonicalKey);
  if (def) return def.description;

  return null;
}

/**
 * Return all canonical market keys, grouped by scope.
 */
export function listMarkets() {
  return {
    universal: Object.keys(UNIVERSAL_MARKETS),
    nhl: Object.keys(NHL_MARKETS).map(k => `nhl:${k}`),
    mlb: Object.keys(MLB_MARKETS).map(k => `mlb:${k}`),
    nfl: Object.keys(_nflMarketsInternal).map(k => `nfl:${k}`),
    // #1655 B1 (Ultron P3): registered, so listed.
    nba: Object.keys(_nbaMarketsInternal).map(k => `nba:${k}`),
  };
}

/**
 * Return every MARKET_DEFS id (canonical and legacy alias-less defs alike), so drift
 * tests can enumerate the registry without going through a derived capability set.
 * listMarkets() above drops alias-less legacy defs such as `sog` and `anytime_goal`.
 */
export function listMarketDefinitionIds() {
  return [..._definitionIndex.keys()];
}

// Exported for tests and future extension.
//
// NFL_MARKETS is deliberately NOT exported, and market-registry.cjs deliberately
// carries no nfl:* ids. An earlier revision of #1038 added both so the SlipSync AI
// resolver would have league-scoped NFL targets.
//
// SlipSync NFL (docs/superpowers/specs/2026-09-27-slipsync-mgm-nfl-design.md) settled
// it the other way: BetMGM NFL legs (mgm.js, competition 103489) resolve through THIS
// file's alias table -- resolveMarket(term, 'nfl'), imported by
// api/lib/slipsync/market-resolver.js and mgm.js -- with no Haiku call and no
// market_catalog read or write. The registry therefore stays NFL-free, and there is
// nothing to regenerate for the NFL adapter.
//
// That also keeps the model prompt unchanged: market-resolver.js embeds the ENTIRE
// registry id list in the model prompt without league filtering, so adding
// nfl:moneyline / nfl:spread / nfl:total would put three ids with labels semantically
// identical to the universal ones in front of every live NHL and MLB import.
//
// #1655 B1: NBA follows the same rule -- the NBA defs are not exported and the registry
// carries no nba:* ids. SlipSync NBA legs resolve through resolveMarket(term, 'nba')
// via market-resolver.js's alias-only league set.
export { UNIVERSAL_MARKETS, NHL_MARKETS };

/** Normalize a league string to a supported key for DB queries.
 *  null/undefined -> null (fail closed). Unknown strings -> null (fail closed). */
export function normalizeLeague(league) {
  if (league == null) return null;
  const key = String(league).toLowerCase().trim();
  if (!key) return null;
  return LEAGUE_CAPABILITIES[key] ? key : null;
}

/** Returns the Set of known vision type IDs for the given league. */
export function getKnownMarketIds(league) {
  return getLeague(league).knownVisionTypeIds;
}

/** Returns true if the canonical key is a binary (yes/no) market for the given league. */
export function isBinaryMarket(canonicalKey, league) {
  return getLeague(league).binaryMarkets.has(canonicalKey);
}

/** Returns true if the canonical key requires a numeric line value for the given league.
 *  Derives from serverLineRequired — NOT requiresLineInput. */
export function isLineRequired(canonicalKey, league) {
  return getLeague(league).lineRequiredMarkets.has(canonicalKey);
}

/** Returns true if the canonical key supports live stat enrichment for the given league. */
export function isEnrichablePlayerProp(canonicalKey, league) {
  return getLeague(league).enrichablePlayerProps.has(canonicalKey);
}

/** Returns true if the canonical key is a moneyline market for score-diff grading. */
export function isMoneylineMarket(canonicalKey, league) {
  return getLeague(league).moneylineMarkets.has(canonicalKey);
}

/** Returns true if the canonical key is a stat-fallback market (unresolvable when no line/binary signal). */
export function isStatFallbackMarket(canonicalKey, league) {
  return getLeague(league).statFallbackMarkets.has(canonicalKey);
}

/** MLB market definitions — derived from MARKET_DEFS at module load time. */
export const MLB_MARKETS = Object.fromEntries(
  MARKET_DEFS
    .filter(d => d.league === 'mlb' && d.aliases.length > 0)
    .map(d => [d.id.replace('mlb:', ''), d])
);

// ---------------------------------------------------------------------------
// Vision-layer type ID sets — use league-keyed functions instead
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Market classification helpers — canonical market key resolution for rendering
// ---------------------------------------------------------------------------

/**
 * Extract the canonical market key from a bet/leg object.
 * Priority: canonicalMarket → type → market → ''
 */
export function marketKey(obj) {
  return obj?.canonicalMarket || obj?.type || obj?.market || '';
}

/**
 * Display-friendly market key — maps canonical keys to legacy short IDs
 * used by UI components (e.g. 'nhl:moneyline' → 'ml').
 */
export function displayMarketKey(obj) {
  const key = marketKey(obj);
  return LEGACY_MARKET_ALIAS[key] || key;
}

/**
 * Returns true for binary yes/no player props (anytime scorer, etc.)
 * that never have a line or overUnder.
 */
export function isBinaryPlayerProp(obj) {
  const key = displayMarketKey(obj);
  return key === 'anytime_goal' || key === 'anytime_point' ||
    key === 'anytime_assist' || key === 'nhl:anytime_assist' ||
    key === 'nfl:anytime_td';
}

// ---------------------------------------------------------------------------
// Public API — new definition-based functions
// ---------------------------------------------------------------------------

/**
 * Return the full definition object for a market ID or bet/leg object.
 * Tries direct lookup, then display alias.
 */
export function getMarketDefinition(idOrObj, league) {
  const key = typeof idOrObj === 'string' ? idOrObj : marketKey(idOrObj);
  const def = _definitionIndex.get(key);
  if (def) return def;
  const display = LEGACY_MARKET_ALIAS[key] || key;
  return _definitionIndex.get(display) || null;
}

/**
 * Return the display label for a market.
 * Accepts a string ID or bet/leg object.
 */
export function getMarketLabel(idOrObj, league) {
  const display = typeof idOrObj === 'string'
    ? (LEGACY_MARKET_ALIAS[idOrObj] || idOrObj)
    : displayMarketKey(idOrObj);
  const def = _definitionIndex.get(display);
  if (def) return def.label;
  // Fall back to direct key lookup for canonical IDs without display alias
  const key = typeof idOrObj === 'string' ? idOrObj : marketKey(idOrObj);
  const directDef = _definitionIndex.get(key);
  return directDef?.label || null;
}

/**
 * marketFieldText -- the text an editable market field shows (#1671).
 *
 * Single job: turn a stored market value into text a user can read. The import pipeline puts
 * the book's wording in `type` (vision-pipeline.js) and the key in `canonicalMarket`; a value
 * that is a league-prefixed canonical id ("nhl:points") shows its label instead. Book wording,
 * even wording with a colon ("1st Period: Total Goals"), and unknown ids are returned as they
 * are. Display only: the field's edit still sends what the user types.
 */
export function marketFieldText(value) {
  if (typeof value === 'string' && value.includes(':')) return getMarketLabel(value) || value;
  return value ?? '';
}

/**
 * Return the short abbreviation for a market.
 * Accepts a string ID or bet/leg object.
 */
export function getMarketShort(idOrObj, league) {
  const display = typeof idOrObj === 'string'
    ? (LEGACY_MARKET_ALIAS[idOrObj] || idOrObj)
    : displayMarketKey(idOrObj);
  const def = _definitionIndex.get(display);
  if (def) return def.short;
  const key = typeof idOrObj === 'string' ? idOrObj : marketKey(idOrObj);
  const directDef = _definitionIndex.get(key);
  return directDef?.short || null;
}

/**
 * Return display market types for the given league, filtered by category.
 *
 * @param {string} league   'nhl' | 'mlb'
 * @param {object} [opts]   { category: 'team' | 'player' } — omit for all
 * @returns {Array<{id, label, short, needsLine}>}
 */
export function getDisplayMarketTypes(league, opts = {}) {
  const { category } = opts;
  if (league == null || String(league).trim() === '') return [];
  const l = String(league).toLowerCase().trim();

  // Per-league display type ID lists. NHL is the only league with display
  // types defined. When MLB display types are added, they go here.
  const leagueDisplayIds = {
    nhl: { team: _displayTeamIds, player: _displayPlayerIds },
    mlb: { team: ['mlb:moneyline'], player: _displayMlbPlayerIds },
  };

  const leagueIds = Object.hasOwn(leagueDisplayIds, l) ? leagueDisplayIds[l] : null;
  if (!leagueIds) return [];

  const ids = category === 'team' ? leagueIds.team
    : category === 'player' ? leagueIds.player
      : [...leagueIds.team, ...leagueIds.player];

  return ids.map(id => {
    const def = _definitionIndex.get(id);
    if (!def) return null;
    return { id: def.id, label: def.label, short: def.short, needsLine: def.requiresLineInput };
  }).filter(Boolean);
}

/**
 * Returns true if the market is a player prop.
 * Uses displayMarketKey for canonical → legacy resolution, matching the
 * existing PLAYER_PROP_TYPES.some() pattern.
 */
export function isPlayerPropMarket(idOrObj, league) {
  const display = typeof idOrObj === 'string'
    ? (LEGACY_MARKET_ALIAS[idOrObj] || idOrObj)
    : displayMarketKey(idOrObj);
  const def = _definitionIndex.get(display);
  return def ? !!def.playerProp : false;
}

/**
 * Returns true if the market pick is a team name that should be normalized
 * via normalizeTeamToken(). Replaces ML_TYPES in vision-pipeline.js.
 */
export function isTeamPickMarket(market, league) {
  const key = typeof market === 'string' ? market : marketKey(market);
  const def = _definitionIndex.get(key);
  if (def) return !!def.teamPick;
  const display = LEGACY_MARKET_ALIAS[key] || key;
  const displayDef = _definitionIndex.get(display);
  return displayDef ? !!displayDef.teamPick : false;
}

// ---------------------------------------------------------------------------
// #699 G2 — Market support classification (fail-closed enforcement)
//
// Source of truth: docs/mlb-coverage-matrix.md. These maps encode the ratified
// per-league classification consumed by the import gate (G2) and, later, by
// settlement. The predicate is intentionally league-specific: a universal
// market (e.g. `spread`) may be SUPPORTED for one league and only
// SUPPORTED_PENDING_FIXTURE for another.
// ---------------------------------------------------------------------------

// Universal canonical IDs that are legitimately reachable for a non-NHL league
// via the league-agnostic alias fallthrough in resolveMarket(). These are NOT
// present in a non-NHL league's knownVisionTypeIds set (which only holds
// league-scoped defs), so isKnownCanonicalMarket() must allow them explicitly.
const _APPROVED_UNIVERSAL_IDS = new Set(['moneyline', 'spread', 'over_under', 'points']);

/**
 * Returns true if `market` is a canonical market ID that SlipTrack recognises
 * for the given league — either a league-scoped def (nhl:* / mlb:*) or an
 * approved universal ID reachable via the league-agnostic fallthrough.
 *
 * This is a recognition check, NOT a support check. `points` is a known
 * canonical for MLB (recognised) even though it is UNSUPPORTED_FAIL_CLOSED.
 * Fail closed: null/empty/unknown-league → false.
 */
export function isKnownCanonicalMarket(market, league) {
  if (!market || typeof market !== 'string') return false;
  const leagueKey = normalizeLeague(league);
  if (!leagueKey) return false;
  if (getLeague(leagueKey).knownVisionTypeIds.has(market)) return true;
  // Approved universal IDs are recognised for any supported league.
  return _APPROVED_UNIVERSAL_IDS.has(market);
}

// Per-league support classification. Keys are canonical market IDs.
// Anything not listed for a league falls through to UNKNOWN (fail closed).
// UNSUPPORTED_FAIL_CLOSED entries include unregistered market IDs that vision /
// sync may emit (mlb:nrfi, mlb:first_five_innings) so the gate can distinguish
// them from truly-unknown markets.
//
// ⚠️ MIRRORED in `api/lib/slipsync/market-support-status.cjs` (the SlipSync CJS
// path cannot import this ESM module). On ANY change to this table, update the
// CJS mirror in the same PR — parity is guarded by
// `src/__tests__/slipsync-market-support-status.test.js` (#699 G3).
// Exported so the SlipSync CJS parity test (slipsync-market-support-status.test.js)
// can iterate the FULL table both ways against market-support-status.cjs (#699 G3).
export const _MARKET_SUPPORT_STATUS = {
  nhl: {
    'nhl:moneyline': 'SUPPORTED',
    moneyline: 'SUPPORTED',
    'nhl:goals': 'SUPPORTED',
    'nhl:assists': 'SUPPORTED',
    'nhl:points': 'SUPPORTED',
    'nhl:shots_on_goal': 'SUPPORTED',
    // #792: SUPPORTED. #699 G3 had labelled it SUPPORTED_PENDING_FIXTURE because the
    // recovery sweep keyed on the unscoped alias only; #792 added the getPlayerStat
    // case (nhl.js) and the RECOVERY_SWEEP_MARKETS entry, so the scoped id now freezes
    // assists like 'nhl:assists'. Mirrored in market-support-status.cjs.
    'nhl:anytime_assist': 'SUPPORTED',
    spread: 'SUPPORTED',
    over_under: 'SUPPORTED',
    // #324 Rung 2: canonicals the registry can produce for NHL that have NO runtime
    // grading path (GPT pre-audit Finding 2 — confirmed against calcStatus,
    // settlement-processor.js, and market_catalog rows). Not SUPPORTED_PENDING_FIXTURE
    // (that implies grading logic exists but lacks fixtures) — these have no grading
    // path at all, so fail closed. Closes the Rung 1 coordination-guard allowlist.
    // `points` here is the universal team-total id (folds into NHL via
    // deriveLeagueCapabilities); NHL team-total grading does not exist. The existing
    // NHL 'points' bet legs are player-props that entered via vision before this
    // classification and are unaffected by this entry.
    'nhl:saves': 'UNSUPPORTED_FAIL_CLOSED',
    'nhl:blocks': 'UNSUPPORTED_FAIL_CLOSED',
    'nhl:hits': 'UNSUPPORTED_FAIL_CLOSED',
    'nhl:pim': 'UNSUPPORTED_FAIL_CLOSED',
    points: 'UNSUPPORTED_FAIL_CLOSED',
  },
  mlb: {
    // Fixture-backed. The league-scoped id grades and is SUPPORTED.
    'mlb:moneyline': 'SUPPORTED',
    // #324 Rung 2 (supersedes #699 G3): after deriveLeagueCapabilities() folds the
    // universal 'moneyline' into MLB, isMoneylineMarket('moneyline','mlb') is TRUE, so
    // the league-agnostic score-diff branch in calcStatus grades it correctly. The
    // universal id is therefore SUPPORTED for MLB (was UNSUPPORTED_FAIL_CLOSED only
    // because the fold was NHL-only, leaving the resolver able to emit an ungradeable
    // universal id for an MLB leg — that footgun is now structurally closed).
    moneyline: 'SUPPORTED',
    // Universal fallthrough — logic exists, no confirmed_parses fixtures
    spread: 'SUPPORTED_PENDING_FIXTURE',
    over_under: 'SUPPORTED_PENDING_FIXTURE',
    // Player props — resolver + registry defined, no fixtures
    'mlb:hits': 'SUPPORTED_PENDING_FIXTURE',
    'mlb:home_runs': 'SUPPORTED_PENDING_FIXTURE',
    'mlb:rbis': 'SUPPORTED_PENDING_FIXTURE',
    'mlb:runs': 'SUPPORTED_PENDING_FIXTURE',
    'mlb:total_bases': 'SUPPORTED_PENDING_FIXTURE',
    'mlb:strikeouts_pitcher': 'SUPPORTED_PENDING_FIXTURE',
    'mlb:strikeouts_batter': 'SUPPORTED_PENDING_FIXTURE',
    // No runtime grading path — fail closed
    points: 'UNSUPPORTED_FAIL_CLOSED',
    'mlb:nrfi': 'UNSUPPORTED_FAIL_CLOSED',
    'mlb:first_five_innings': 'UNSUPPORTED_FAIL_CLOSED',
    // Catch-all player prop that resolves to nothing known
    'mlb:player_props_generic': 'UNKNOWN',
  },
  // #1040 PR D + #1353 PR 2: the four box-score O/U props + nfl:anytime_td +
  // nfl:moneyline + nfl:spread + nfl:total (eight) are SUPPORTED -- nfl is in
  // SETTLEABLE_LEAGUES and these are GRADABLE_MARKETS_BY_LEAGUE.nfl. Everything else
  // NFL fails closed: nfl:first_td / nfl:last_td (scoringPlays spike).
  // Promote an entry ONLY together with the admission map, the def flags above, the
  // adapter sweep list (props) and docs/nfl-coverage-matrix.md.
  nfl: {
    'nfl:moneyline': 'SUPPORTED',
    'nfl:spread': 'SUPPORTED',
    'nfl:total': 'SUPPORTED',
    'nfl:passing_yards': 'SUPPORTED',
    'nfl:rushing_yards': 'SUPPORTED',
    'nfl:receiving_yards': 'SUPPORTED',
    'nfl:receptions': 'SUPPORTED',
    'nfl:anytime_td': 'SUPPORTED',
    'nfl:first_td': 'UNSUPPORTED_FAIL_CLOSED',
    'nfl:last_td': 'UNSUPPORTED_FAIL_CLOSED',
    // Universal ids are carved OUT of NFL capabilities (see
    // deriveLeagueCapabilities), so resolveMarket cannot emit them for an NFL
    // leg. Listed anyway so that if one ever arrives it fails closed with the
    // correct warning code rather than falling through to 'UNKNOWN'.
    moneyline: 'UNSUPPORTED_FAIL_CLOSED',
    spread: 'UNSUPPORTED_FAIL_CLOSED',
    over_under: 'UNSUPPORTED_FAIL_CLOSED',
    points: 'UNSUPPORTED_FAIL_CLOSED',
  },
  // #1655 B1: every NBA market fails closed until D1 (game markets) / D2 (props) admit
  // it. Promote an entry ONLY together with SETTLEABLE_LEAGUES,
  // GRADABLE_MARKETS_BY_LEAGUE.nba, the def flags above, the CJS mirror and
  // docs/nba-coverage-matrix.md. The bare universal ids are carved out of nba
  // (LEAGUE_SCOPED_ONLY_LEAGUES) and listed, as for nfl, so one that arrives anyway
  // fails closed with the right warning code rather than 'UNKNOWN'.
  nba: {
    'nba:moneyline': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:spread': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:total': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:points': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:rebounds': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:assists': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:threes_made': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:pra': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:points_rebounds': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:points_assists': 'UNSUPPORTED_FAIL_CLOSED',
    'nba:rebounds_assists': 'UNSUPPORTED_FAIL_CLOSED',
    moneyline: 'UNSUPPORTED_FAIL_CLOSED',
    spread: 'UNSUPPORTED_FAIL_CLOSED',
    over_under: 'UNSUPPORTED_FAIL_CLOSED',
    points: 'UNSUPPORTED_FAIL_CLOSED',
  },
};

/**
 * Classify a canonical market's settlement support for a given league.
 *
 * Source of truth: docs/mlb-coverage-matrix.md.
 * Returns one of: 'SUPPORTED' | 'SUPPORTED_PENDING_FIXTURE'
 *                 | 'UNSUPPORTED_FAIL_CLOSED' | 'UNKNOWN'.
 *
 * Fail closed: a null/empty canonical, an unknown league, or a canonical not
 * listed for the (recognised) league all return 'UNKNOWN'. The distinction
 * between UNSUPPORTED_FAIL_CLOSED and UNKNOWN drives the per-leg warning code,
 * not the save/no-save action (both save-as-unresolvable at import).
 */
export function marketSupportStatus(market, league) {
  if (!market || typeof market !== 'string') return 'UNKNOWN';
  const leagueKey = normalizeLeague(league);
  if (!leagueKey) return 'UNKNOWN';
  const table = Object.hasOwn(_MARKET_SUPPORT_STATUS, leagueKey)
    ? _MARKET_SUPPORT_STATUS[leagueKey]
    : null;
  if (!table) return 'UNKNOWN';
  return Object.hasOwn(table, market) ? table[market] : 'UNKNOWN';
}
